import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { startHeroStatsAnimation } from "../index.js";
import { Handshake, Dices, Ticket, Shuffle, Trophy } from "lucide-react";

const HeroStats = ({
  bookies,
  lotteries,
  buytotal,
  draws,
  wins,
  startAnimation,
}) => {
  const [displayValues, setDisplayValues] = useState([0, 0, 0, 0, 0]);

  const stats = [
    {
      title: "Creators",
      value: bookies,
      icon: Handshake,
    },
    {
      title: "Lotteries",
      value: lotteries,
      icon: Dices,
    },
    {
      title: "Tickets Sold",
      value: buytotal,
      icon: Ticket,
    },
    {
      title: "Draws",
      value: draws,
      icon: Shuffle,
    },
    {
      title: "Wins",
      value: wins,
      icon: Trophy,
    },
  ];

  useEffect(() => {
    if (!startAnimation) {
      setDisplayValues([
        Number(bookies) || 0,
        Number(lotteries) || 0,
        Number(buytotal) || 0,
        Number(draws) || 0,
        Number(wins) || 0,
      ]);
      return;
    }

    const cleanup = startHeroStatsAnimation(stats, setDisplayValues);
    return cleanup;
  }, [startAnimation, bookies, lotteries, buytotal, draws, wins]);

  return (
    <Box
      className="home-hero-stats"
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, minmax(0, 1fr))",
          sm: "repeat(3, minmax(0, 1fr))",
          md: "repeat(5, minmax(0, 1fr))",
        },
        gap: { xs: "12px", sm: "14px", md: "16px" },
        width: "100%",
        maxWidth: "850px",
        mt: { xs: 1, md: 1.5 },
        mb: { xs: 1, md: 1.5 },
      }}
    >
      {stats.map((stat) => {
        const Icon = stat.icon;

        return (
          <Box
            key={stat.title}
            className="hero-stat-item"
            sx={{
              position: "relative",
              minWidth: 0,
              minHeight: { xs: "110px", sm: "120px" },
              p: { xs: "12px 8px", sm: "14px 10px" },
              borderRadius: "20px",
              background: (theme) => theme.custom.colors.alpha.white[82],
              border: (theme) =>
                `1px solid ${theme.custom.colors.alpha.white[95]}`,
              boxShadow: "0 12px 35px rgba(20, 33, 43, 0.08)",
              backdropFilter: "blur(12px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              overflow: "hidden",
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
              "&:hover": {
                transform: "translateY(-6px)",
                boxShadow: "0 18px 40px rgba(20, 33, 43, 0.14)",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: "20%",
                width: "60%",
                height: "3px",
                borderRadius: "10px 10px 0 0",
                background: (theme) => theme.palette.primary.main,
                opacity: 0.8,
              },
            }}
          >
            <Box
              sx={{
                width: { xs: "42px", sm: "48px" },
                height: { xs: "42px", sm: "48px" },
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: "10px",
                flexShrink: 0,
                color: (theme) => theme.palette.primary.main,
              }}
            >
              <Icon size={24} strokeWidth={1.8} />
            </Box>

            <Typography
              component="p"
              sx={{
                margin: 0,
                fontFamily: "inherit",
                fontSize: { xs: "12px", sm: "13px" },
                fontWeight: 600,
                color: (theme) => theme.palette.text.secondary,
                lineHeight: 1.3,
                whiteSpace: "nowrap",
              }}
            >
              {stat.title}
            </Typography>

            <Typography
              component="span"
              sx={{
                marginTop: "5px",
                fontFamily: "inherit",
                fontSize: { xs: "25px", sm: "28px" },
                fontWeight: 700,
                color: (theme) => theme.palette.text.primary,
                lineHeight: 1,
              }}
            >
              {displayValues[stats.indexOf(stat)]}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default HeroStats;